import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Building2, 
  Users, 
  Mail, 
  Phone, 
  MapPin, 
  CheckCircle, 
  XCircle, 
  Edit,
  Loader2,
  ChevronRight,
  ChevronDown
} from 'lucide-react';

import { supabase } from '../../lib/supabase';
import { Button } from '../../components/ui/Button';
import { PageHeaderHost } from '../../components/layout/PageHeaderHost';
import CompanyEditModal from './CompanyEditModal';
import CompanyMembersView from './CompanyMembersView';

interface CompanyResult {
  id: string;
  name: string | null;
  company_email: string | null;
  city: string | null;
  state: string | null;
  is_active: boolean;
  setup_complete: boolean;
  created_at: string;
  phone: string | null;
  contact_fn: string | null;
  contact_ln: string | null;
  address?: string | null;
  address2?: string | null;
  zip?: string | null;
  slogan?: string | null;
  ein?: string | null;
  logo_url?: string | null;
}

interface CompanyUser {
  user_id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  role: string;
  onboarding_complete: boolean;
  created_at: string;
}

export default function CompanySearchPortal() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [companies, setCompanies] = useState<CompanyResult[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<CompanyResult[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyResult | null>(null);
  const [fullCompanyData, setFullCompanyData] = useState<CompanyResult | null>(null);
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [expandedCompany, setExpandedCompany] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMembersView, setShowMembersView] = useState(false);

  // Load all companies on mount
  useEffect(() => {
    loadCompanies();
  }, []);

  // Filter companies based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCompanies(companies);
      return;
    }

    const query = searchQuery.trim().toLowerCase();
    const filtered = companies.filter((company) => {
      const name = company.name?.toLowerCase() || '';
      const email = company.company_email?.toLowerCase() || '';
      const city = company.city?.toLowerCase() || '';
      const state = company.state?.toLowerCase() || '';
      const phone = company.phone?.toLowerCase() || '';
      const contactName = `${company.contact_fn || ''} ${company.contact_ln || ''}`.toLowerCase();

      return (
        name.includes(query) ||
        email.includes(query) ||
        city.includes(query) ||
        state.includes(query) ||
        phone.includes(query) ||
        contactName.includes(query)
      );
    });

    setFilteredCompanies(filtered);
  }, [searchQuery, companies]);

  const loadCompanies = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('companies')
        .select('id, name, company_email, city, state, is_active, setup_complete, created_at, phone, contact_fn, contact_ln')
        .order('created_at', { ascending: false });

      if (fetchError) {
        setError(fetchError.message);
        return;
      }

      setCompanies(data || []);
      setFilteredCompanies(data || []);
    } catch (err) {
      console.error('[CompanySearchPortal] Load error:', err);
      setError(err instanceof Error ? err.message : 'load_failed');
    } finally {
      setLoading(false);
    }
  };

  const loadFullCompanyData = async (companyId: string) => {
    try {
      const { data, error: fetchError } = await supabase
        .from('companies')
        .select('id, name, company_email, city, state, is_active, setup_complete, created_at, phone, contact_fn, contact_ln, address, address2, zip, slogan, ein, logo_url')
        .eq('id', companyId)
        .single();

      if (fetchError) {
        setError(fetchError.message);
        return null;
      }

      return data as CompanyResult;
    } catch (err) {
      console.error('[CompanySearchPortal] Load full company error:', err);
      setError(err instanceof Error ? err.message : 'load_failed');
      return null;
    }
  };

  const handleEditClick = async () => {
    if (!selectedCompany) return;
    const fullData = await loadFullCompanyData(selectedCompany.id);
    if (fullData) {
      setFullCompanyData(fullData);
      setShowEditModal(true);
    }
  };

  const handleEditSave = () => {
    loadCompanies();
    if (selectedCompany) {
      loadFullCompanyData(selectedCompany.id).then((data) => {
        if (data) {
          setSelectedCompany(data);
        }
      });
    }
  };

  const loadCompanyUsers = async (companyId: string) => {
    setLoadingUsers(true);
    try {
      const { data: usersData, error: usersError } = await supabase
        .from('user_company_relationships')
        .select(`
          user_id,
          onboarding_complete,
          created_at,
          system_roles!inner(role),
          user_profiles!inner(email, first_name, last_name)
        `)
        .eq('company_id', companyId);

      if (usersError) throw usersError;

      type RelationshipRow = {
        user_id: string;
        onboarding_complete?: boolean | null;
        created_at?: string | null;
        system_roles?: { role: string } | null;
        user_profiles?: { first_name?: string | null; last_name?: string | null; email?: string | null } | null;
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const formattedUsers: CompanyUser[] = ((usersData || []) as any[]).map((rel: RelationshipRow) => ({
        user_id: rel.user_id,
        email: rel.user_profiles?.email || null,
        first_name: rel.user_profiles?.first_name || null,
        last_name: rel.user_profiles?.last_name || null,
        role: rel.system_roles?.role || 'unknown',
        onboarding_complete: rel.onboarding_complete || false,
        created_at: rel.created_at || new Date().toISOString(),
      } as CompanyUser));

      setCompanyUsers(formattedUsers);
    } catch (error) {
      console.error('[CompanySearchPortal] Error loading company users:', error);
      setCompanyUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleCompanySelect = (company: CompanyResult) => {
    if (expandedCompany === company.id) {
      setExpandedCompany(null);
      setSelectedCompany(null);
      setCompanyUsers([]);
    } else {
      setExpandedCompany(company.id);
      setSelectedCompany(company);
      loadCompanyUsers(company.id);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toISOString().split('T')[0];
  };

  return (
    <div className="relative">
      {/* Page Header - uses shared AdminNavBarHeader component, positioned absolutely at top */}
      <PageHeaderHost
        title="Company Search"
        icon={<Building2 className="w-6 h-6" />}
        backButton={{
          to: '/dev/dashboard',
          onClick: () => navigate('/dev/dashboard'),
        }}
      />

      {/* Main Content - add padding-top to account for absolute header (h-16 = 64px) */}
      <div className="space-y-6 min-h-0 pt-16">
        {/* Search Section */}
        <section className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A9A3A3] w-5 h-5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search companies by name, email, city, state, phone, or contact name..."
              className="w-full pl-10 pr-4 py-3 bg-[#221F1F] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
              disabled={loading}
            />
          </div>
          {!loading && (
            <div className="text-sm text-[#A9A3A3] mt-2">
              Showing {filteredCompanies.length} of {companies.length} companies
            </div>
          )}
        </section>

        {/* Error Display */}
        {error && (
          <section className="mb-6">
            <div className="bg-red-950/20 border border-red-900 rounded-lg px-4 py-3 text-text-primary text-sm">
              Error: {error}
            </div>
          </section>
        )}

        {/* Companies List */}
        {!loading && filteredCompanies.length > 0 && (
          <section className="mb-6">
            <h2 className="text-xl font-semibold text-[#EBFFEE] mb-4">
              Companies ({filteredCompanies.length})
            </h2>
            <div className="space-y-3">
              {filteredCompanies.map((company) => {
                const isExpanded = expandedCompany === company.id;
                return (
                  <div
                    key={company.id}
                    className="bg-[#221F1F] border border-[#504A4A] rounded-lg overflow-hidden"
                  >
                    {/* Company Header */}
                    <div
                      className="p-4 cursor-pointer hover:bg-[#363232] transition-colors"
                      onClick={() => handleCompanySelect(company)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-[#A9A3A3]" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-[#A9A3A3]" />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                              <h3 className="text-lg font-semibold text-[#EBFFEE]">
                                {company.name || 'Unnamed Company'}
                              </h3>
                              {company.is_active ? (
                                <CheckCircle className="w-5 h-5 text-green-500" />
                              ) : (
                                <XCircle className="w-5 h-5 text-red-500" />
                              )}
                              {company.setup_complete ? (
                                <span className="px-2 py-0.5 bg-green-600/20 text-green-400 text-xs rounded">
                                  Setup Complete
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 bg-orange-600/20 text-orange-400 text-xs rounded">
                                  Setup Pending
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-[#A9A3A3]">
                              {company.company_email && (
                                <span className="flex items-center gap-1">
                                  <Mail size={14} />
                                  {company.company_email}
                                </span>
                              )}
                              {(company.city || company.state) && (
                                <span className="flex items-center gap-1">
                                  <MapPin size={14} />
                                  {[company.city, company.state].filter(Boolean).join(', ')}
                                </span>
                              )}
                              {company.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone size={14} />
                                  {company.phone}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="border-t border-[#504A4A] p-4 bg-[#1A1818]">
                        {loadingUsers ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 size={32} className="animate-spin text-rollops-accent" />
                          </div>
                        ) : (
                          <div className="space-y-6">
                            {/* Company Details */}
                            <div>
                              <h4 className="text-lg font-semibold text-[#EBFFEE] mb-3">Company Details</h4>
                              <div className="bg-[#221F1F] rounded-lg border border-[#504A4A] overflow-hidden">
                                <table className="w-full">
                                  <tbody>
                                    <tr className="border-b border-[#504A4A]">
                                      <td className="px-4 py-2 text-sm text-[#A9A3A3] w-32">ID</td>
                                      <td className="px-4 py-2 text-xs text-[#EBFFEE] font-mono break-all">{company.id}</td>
                                    </tr>
                                    <tr className="border-b border-[#504A4A]">
                                      <td className="px-4 py-2 text-sm text-[#A9A3A3]">Name</td>
                                      <td className="px-4 py-2 text-sm text-[#EBFFEE]">{company.name || 'N/A'}</td>
                                    </tr>
                                    <tr className="border-b border-[#504A4A]">
                                      <td className="px-4 py-2 text-sm text-[#A9A3A3]">Email</td>
                                      <td className="px-4 py-2 text-sm text-[#EBFFEE]">{company.company_email || 'N/A'}</td>
                                    </tr>
                                    <tr className="border-b border-[#504A4A]">
                                      <td className="px-4 py-2 text-sm text-[#A9A3A3]">Phone</td>
                                      <td className="px-4 py-2 text-sm text-[#EBFFEE]">{company.phone || 'N/A'}</td>
                                    </tr>
                                    <tr className="border-b border-[#504A4A]">
                                      <td className="px-4 py-2 text-sm text-[#A9A3A3]">Location</td>
                                      <td className="px-4 py-2 text-sm text-[#EBFFEE]">
                                        {company.city && company.state 
                                          ? `${company.city}, ${company.state}`
                                          : company.city || company.state || 'N/A'}
                                      </td>
                                    </tr>
                                    <tr className="border-b border-[#504A4A]">
                                      <td className="px-4 py-2 text-sm text-[#A9A3A3]">Contact</td>
                                      <td className="px-4 py-2 text-sm text-[#EBFFEE]">
                                        {company.contact_fn && company.contact_ln
                                          ? `${company.contact_fn} ${company.contact_ln}`
                                          : company.contact_fn || company.contact_ln || 'N/A'}
                                      </td>
                                    </tr>
                                    <tr className="border-b border-[#504A4A]">
                                      <td className="px-4 py-2 text-sm text-[#A9A3A3]">Status</td>
                                      <td className="px-4 py-2 text-sm">
                                        <span className={company.is_active ? 'text-green-500' : 'text-red-500'}>
                                          {company.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="px-4 py-2 text-sm text-[#A9A3A3]">Created</td>
                                      <td className="px-4 py-2 text-sm text-[#EBFFEE]">{formatDate(company.created_at)}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            {/* Users Section */}
                            <div>
                              <h4 className="text-lg font-semibold text-[#EBFFEE] mb-3 flex items-center gap-2">
                                <Users size={20} />
                                Company Users ({companyUsers.length})
                              </h4>
                              {companyUsers.length > 0 ? (
                                <div className="bg-[#221F1F] rounded-lg border border-[#504A4A] overflow-hidden">
                                  <table className="w-full">
                                    <thead className="bg-[#363232]">
                                      <tr>
                                        <th className="px-4 py-2 text-left text-xs font-semibold text-[#EBFFEE]">Name</th>
                                        <th className="px-4 py-2 text-left text-xs font-semibold text-[#EBFFEE]">Email</th>
                                        <th className="px-4 py-2 text-left text-xs font-semibold text-[#EBFFEE]">Role</th>
                                        <th className="px-4 py-2 text-left text-xs font-semibold text-[#EBFFEE]">Status</th>
                                        <th className="px-4 py-2 text-left text-xs font-semibold text-[#EBFFEE]">Joined</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {companyUsers.map((user) => (
                                        <tr key={user.user_id} className="border-t border-[#504A4A] hover:bg-[#363232]">
                                          <td className="px-4 py-2 text-sm text-[#EBFFEE]">
                                            {user.first_name || user.last_name 
                                              ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                                              : 'N/A'}
                                          </td>
                                          <td className="px-4 py-2 text-sm text-[#A9A3A3]">{user.email || 'N/A'}</td>
                                          <td className="px-4 py-2">
                                            <span className="px-2 py-0.5 bg-[#363232] text-[#EBFFEE] text-xs rounded capitalize">
                                              {user.role}
                                            </span>
                                          </td>
                                          <td className="px-4 py-2">
                                            {user.onboarding_complete ? (
                                              <CheckCircle className="w-5 h-5 text-green-500" />
                                            ) : (
                                              <XCircle className="w-5 h-5 text-orange-500" />
                                            )}
                                          </td>
                                          <td className="px-4 py-2 text-sm text-[#A9A3A3]">
                                            {formatDate(user.created_at)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="text-sm text-[#A9A3A3]">No users found for this company</p>
                              )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditClick();
                                }}
                                className="border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232]"
                              >
                                <Edit size={16} className="mr-2" />
                                Edit Company
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowMembersView(true);
                                }}
                                className="border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232]"
                              >
                                <Users size={16} className="mr-2" />
                                View All Members
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* No Results */}
        {!loading && filteredCompanies.length === 0 && companies.length > 0 && (
          <section className="mb-6">
            <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg px-4 py-3 text-[#A9A3A3] text-sm">
              No companies found matching search query
            </div>
          </section>
        )}

        {/* Loading State */}
        {loading && (
          <section className="mb-6">
            <div className="flex items-center justify-center py-12">
              <Loader2 size={48} className="animate-spin text-rollops-accent" />
            </div>
          </section>
        )}

        {/* Empty State */}
        {!loading && companies.length === 0 && (
          <section className="mb-6">
            <div className="text-center py-12">
              <Building2 size={48} className="mx-auto text-[#A9A3A3] mb-4" />
              <p className="text-[#A9A3A3]">No companies found</p>
            </div>
          </section>
        )}
      </div>

      {/* Modals */}
      {showEditModal && fullCompanyData && (
        <CompanyEditModal
          company={fullCompanyData}
          onClose={() => {
            setShowEditModal(false);
            setFullCompanyData(null);
          }}
          onSave={handleEditSave}
        />
      )}

      {showMembersView && selectedCompany && (
        <CompanyMembersView
          companyId={selectedCompany.id}
          companyName={selectedCompany.name}
          onClose={() => setShowMembersView(false)}
        />
      )}
    </div>
  );
}

